import { ColumnDef } from '@tanstack/react-table';
import { Field, Form as FormikForm, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { Form, InputGroup, Stack } from 'react-bootstrap';
import * as Yup from 'yup';
import { ButtonData } from '../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { OverrideLegend } from '../../../../components/generic';
import { CourseTypeCell } from '../../../../components/tables/cellComponents/CourseTypeCell';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../../components/tables/cellComponents/YesNoCell';
import { DateFilterField } from '../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import {
  useGetTimesheetOverridesQuery,
  useUpdateTimesheetOverrideMutation
} from '../../../../services/endpoints/finance/timesheets';
import {
  Scopes,
  useGetAdminsQuery,
  useGetUsersQuery
} from '../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../services/helper';
import { timesheetOverride } from '../../../../types/finance.types';
import { hasDashAccess, isGlobal, isLocal, timesheetOverrideTypes } from '../../../../utils/consts';
import { getFilterValue } from '../../../../utils/listFilterSearchParams';
import { formatDateString } from '../../../../utils/utils';
import { getOverrideStatus } from '../../../../components/tables/cellComponents/OverrideStatusCell';
import { Forbidden } from '../../../../components/error/Forbidden';
import { useListFiltersInUrl } from '../../../../hooks';

interface DataRow extends timesheetOverride {}

const defaultScope = 'pending';

const isOverridePending = (override: timesheetOverride) =>
  getOverrideStatus(override) === 'pending';

const ApproveDenyCell = ({
                           onDecision
                         }: {
  onDecision: (values: any, approved: boolean) => void;
}) => {
  const { values } = useFormikContext<any>();
  return (
      <Stack direction="horizontal" gap={2}>
        <SimpleButton
            height={45}
            type="button"
            className="btn-sm text-center"
            variant="primary"
            onClick={() => onDecision(values, true)}
        >
          Approve
        </SimpleButton>
        <SimpleButton
            height={45}
            type="button"
            className="btn-sm text-center"
            variant="danger"
            onClick={() => onDecision(values, false)}
        >
          Deny
        </SimpleButton>
      </Stack>
  );
};

export const OverridesView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const currentUser = getCurrentUser();
  const has_admin_access = hasDashAccess(currentUser?.user_type_id!);
  const isGlobalUser = isGlobal(currentUser?.user_type_id!);
  const isLocalUser = isLocal(currentUser?.user_type_id!);

  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const { data, error, isLoading, isFetching } = useGetTimesheetOverridesQuery(filters, {
    skip: !isLocalUser
  });
  const [updateTimesheetOverride] = useUpdateTimesheetOverrideMutation();
  const {
    data: instructors,
    error: instructorsError,
    isLoading: instructorsLoading
  } = useGetUsersQuery(
    {
      scope: 'colleagues',
      data: { entries: 10000, orderBy: { field: 'first_name', order: 'asc' } }
    },
    { skip: !isLocalUser }
  );
  const {
    data: admins,
    error: adminsError,
    isLoading: adminsLoading
  } = useGetAdminsQuery(
    { data: { orderBy: { field: 'first_name', order: 'asc' } } },
    { skip: !isLocalUser }
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const overrides = data?.results ?? [];

  const instructorOptions =
    instructors?.results.map((i) => ({ value: i.id, label: i.full_name })) ?? [];
  const adminOptions = admins?.results.map((r) => ({ value: r.id, label: r.full_name })) ?? [];
  const overrideTypeOptions =
    timesheetOverrideTypes?.map((tot, idx) => ({ value: idx + 1, label: tot })) ?? [];

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'location_id',
      label: 'Location',
      type: 'number',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          )}
        />
      )
    },
    {
      name: 'instructor_id',
      label: 'Instructor',
      type: 'number',
      element: (
        <FormSelect
          name="instructor_id"
          label="Instructor"
          options={instructorOptions}
          loadingError={instructorsError ? 'Error loading instructors' : undefined}
          selected_Value={instructorOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'instructor_id')
          )}
        />
      )
    },
    {
      name: 'admin_id',
      label: 'Admin',
      type: 'number',
      element: (
        <FormSelect
          name="admin_id"
          label="Processed By"
          options={adminOptions}
          loadingError={adminsError ? 'Error loading admins' : undefined}
          selected_Value={adminOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'admin_id')
          )}
        />
      )
    },
    {
      name: 'timesheet_override_type',
      label: 'Timesheet Override Type',
      type: 'number',
      element: (
        <FormSelect
          name="timesheet_override_type"
          label="Timesheet Override Type"
          options={overrideTypeOptions}
          selected_Value={overrideTypeOptions.find(
            (option) =>
              String(option.value) ===
              getFilterValue(filters.data?.filterBy, 'timesheet_override_type')
          )}
        />
      )
    },
    {
      name: 'denial_notes',
      label: 'Denial Notes',
      value: getFilterValue(filters.data?.filterBy, 'denial_notes')
    },
    {
      name: 'override_request',
      label: 'Override Request',
      value: getFilterValue(filters.data?.filterBy, 'override_request')
    },
    {
      name: 'date_from',
      id: 'date',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="date_from"
          label="Class Date Start"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'date', 'greater_equal_than')
    },
    {
      name: 'date_to',
      id: 'date',
      operator: 'lesser_equal_than',
      element: (
        <DateFilterField
          name="date_to"
          label="Class Date End"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'date', 'lesser_equal_than')
    },
    {
      name: 'approved_from',
      id: 'approved_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="approved_from"
          label="Approved At Start"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'approved_at', 'greater_equal_than')
    },
    {
      name: 'approved_to',
      id: 'approved_at',
      operator: 'lesser_equal_than',
      element: (
        <DateFilterField
          name="approved_to"
          label="Approved At End"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'approved_at', 'lesser_equal_than')
    }
  ];

  if (!isGlobalUser) {
    // remove location filter
    filterFormFields.splice(1, 1);
  }

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ getValue, row }) => {
        return (
          <>
            <FormikForm id={`form_${row.original.id}`}></FormikForm>
            {getValue()}
          </>
        );
      }
    },
    {
      header: 'Location',
      accessorKey: 'lesson.course.venue.location.title',
      enableSorting: false
    },
    {
      header: 'Class',
      accessorKey: 'lesson.date',
      enableSorting: false,
      cell: ({ getValue }) => formatDateString(getValue<string>())
    },
    {
      header: 'Session',
      accessorKey: 'lesson.course',
      enableSorting: false,
      cell: ({ getValue, row }) => {
        let value: { id: number; is_online: boolean; title: string } = getValue() as any;
        if (value) {
          return (
            <>
              <CourseTypeCell
                is_online={value.is_online}
                value={
                  <LinkCell
                    content={value.title}
                    url={`/admin/schedule/sessions/${value.id}`}
                  />
                }
              />
            </>
          );
        }
      }
    },
    {
      header: 'Instructor',
      accessorKey: 'instructor.full_name',
      enableSorting: false,
      cell: ({ getValue, row }) => `${getValue()} (${row.original.role ?? ''})`
    },
    {
      header: 'Override Type/Request',
      accessorKey: 'timesheet_override_type',
      enableSorting: false,
      cell: ({ row }) => (
        <>
          <b>
            {row.original.timesheet_override_type
              ? timesheetOverrideTypes[row.original.timesheet_override_type - 1]
              : ''}
            :
          </b>{' '}
          {row.original.override_request}
        </>
      )
    },
    {
      header: 'Standard\nReimbursement',
      accessorKey: 'standard_reimbursement',
      cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
      header: 'Override\nReimbursement',
      accessorKey: 'override_reimbursement',
      cell: ({ getValue, row }) =>
        has_admin_access && isOverridePending(row.original) ? (
          <Form.Group
            controlId={`override_reimbursement_${row.original.id}`}
            className={'input-group input-group-sm'}
          >
            <InputGroup.Text>$</InputGroup.Text>
            <Field
              name="override_reimbursement"
              className={'form-control'}
            ></Field>
          </Form.Group>
        ) : (
          <CurrencyCell value={getValue<string>()} />
        )
    },
    {
      header: 'Standard\nRate',
      accessorKey: 'standard_rate',
      cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
      header: 'Override Rate',
      accessorKey: 'override_rate',
      cell: ({ getValue, row }) =>
        has_admin_access && isOverridePending(row.original) ? (
          <Form.Group
            controlId={`override_rate_${row.original.id}`}
            className="input-group input-group-sm"
          >
            <InputGroup.Text>$</InputGroup.Text>
            <Field
              name="override_rate"
              className="form-control"
            />
          </Form.Group>
        ) : (
          <CurrencyCell value={getValue<string>()} />
        )
    },
    {
      header: 'Approved',
      accessorKey: 'override_approved',
      cell: ({ getValue, row }) =>
        has_admin_access && isOverridePending(row.original) ? (
            <></>
        ) : (
          <YesNoCell value={getValue<boolean>()} />
        )
    },
    {
      header: 'Denial Notes',
      accessorKey: 'denial_notes',
      cell: ({ getValue, row }) =>
        has_admin_access && isOverridePending(row.original) ? (
          <Form.Group controlId={`denial_notes_${row.original.id}`}>
            <Field
              as="textarea"
              rows={4}
              cols={55}
              name="denial_notes"
              className={'form-control'}
            ></Field>
          </Form.Group>
        ) : (
          getValue()
        )
    },
    {
      header: '',
      accessorKey: 'blank',
      enableSorting: false,
      cell: ({ getValue, row }) =>
        has_admin_access && isOverridePending(row.original) ? (
            <ApproveDenyCell onDecision={submitDecision} />
        ) : (
          getValue()
        )
    }
  ];

  useEffect(() => {
    if (!data) return;
    if (data.scopes) {
      setScopes(
        data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
      );
      setButtons(
        data.scopes.map((sMap) =>
          Object.entries(sMap).map(([scope, title]) => {
            let scope_count = 0;
            if (data.scope_counts != null) {
              scope_count = data.scope_counts[scope] ?? 0;
            }
            return {
              title: `${title} (${scope_count})`,
              key: scope
            };
          })
        )
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      addToast(`Error while loading data: ${JSON.stringify(error)}`, 'error');
    }
  }, [error]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const tableValidationSchema = Yup.object({
    override_id: Yup.number(),
    override_reimbursement: Yup.string(),
    override_rate: Yup.string(),
    denial_notes: Yup.string()
  });

  const submitDecision = (values: any, approved: boolean) => {
    const payload: any = {
      override_id: values.override_id,
      override_approved: approved
    };
    if (values.override_rate !== '' && values.override_rate != null) {
      payload.override_rate = Number(values.override_rate);
    }
    if (values.override_reimbursement !== '' && values.override_reimbursement != null) {
      payload.override_reimbursement = Number(values.override_reimbursement);
    }
    if (!approved && values.denial_notes) {
      payload.denial_notes = values.denial_notes;
    }
    updateTimesheetOverride(payload)
        .unwrap()
        .then(() => {
          addToast(approved ? 'Override approved' : 'Override denied', 'success');
        })
        .catch((error: any) => {
          console.log(error);
          addToast(`Failed to update timesheet override: ${JSON.stringify(error)}`, 'error');
        });
  };

  if (!isLocalUser) {
    return <Forbidden />;
  }

  if (error || isLoading) {
    return <></>;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={scopes[filters?.scope!]}
        columns={columns}
        data={overrides}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="timesheet-overrides"
        filters={filters?.data?.filterBy}
        sidebars={[<OverrideLegend />]}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        rowFormProps={{
          validationSchema: tableValidationSchema,
          onSubmit: () => {},
          getInitialValues: (row) => ({
            override_id: row.original.id,
            override_rate: row.original.override_rate ?? '',
            override_reimbursement: row.original.override_reimbursement ?? '',
            denial_notes: row.original.denial_notes ?? ''
          })
        }}
      />
    </div>
  );
};
